import { useContext, useRef, useState } from "react";
import { formatarDataCompleta } from "../../utils/functions";
import Button from "../../atoms/button";
import { MAX_ATTACHMENT_BYTES } from "../functions";
import { getOrderAdapter } from "../../orders/orderAdapters";
import ToastContext from "../../../context/toastProvider";
import AuthContext from "../../../context/authProvider";
import ChevronIcon from "../../../img/chevronIcon";

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatEta = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "—";
    if (seconds < 1) return "< 1s";
    if (seconds < 60) return `${Math.ceil(seconds)}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.ceil(seconds % 60);
    return `${m}m ${s}s`;
};

const makeQueueId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const uploadWithProgress = (url, file, contentType, onProgress) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const startedAt = Date.now();

        xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            const elapsedSec = Math.max((Date.now() - startedAt) / 1000, 0.001);
            const rate = event.loaded / elapsedSec;
            const remainingBytes = event.total - event.loaded;
            const etaSec = rate > 0 ? remainingBytes / rate : Infinity;
            onProgress({
                loaded: event.loaded,
                total: event.total,
                percent: Math.min(100, Math.round((event.loaded / event.total) * 100)),
                etaSec,
            });
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`Upload falhou (${xhr.status})`));
            }
        };
        xhr.onerror = () => reject(new Error("Erro de rede no upload"));
        xhr.onabort = () => reject(new Error("Upload cancelado"));

        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.send(file);
    });
};

const triggerBrowserDownload = (blob, filename) => {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename || "anexo";
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const gradingAdapter = getOrderAdapter("grading");

const ProductAttachments = ({ productData, refresh, adapter = gradingAdapter }) => {
    const { goodToast, badToast } = useContext(ToastContext);
    const { user } = useContext(AuthContext);

    const [more, setMore] = useState(false);
    const [queue, setQueue] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null);
    const [uploadPhase, setUploadPhase] = useState("");
    const fileInputRef = useRef(null);

    const attachments = productData?.attachments;

    const addFiles = (fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;

        const next = [];
        let rejected = 0;
        for (const file of incoming) {
            if (file.size > MAX_ATTACHMENT_BYTES) {
                rejected += 1;
                continue;
            }
            next.push({
                id: makeQueueId(),
                file,
                name: file.name,
            });
        }
        if (rejected) {
            badToast(`${rejected} arquivo(s) acima de 50 MB foram ignorados`);
        }
        if (next.length) {
            setQueue((prev) => [...prev, ...next]);
        }
    };

    const handleFileChange = (e) => {
        addFiles(e.target.files);
        e.target.value = "";
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (uploading) return;
        addFiles(e.dataTransfer.files);
    };

    const updateQueueName = (id, name) => {
        setQueue((prev) => prev.map((item) => (item.id === id ? { ...item, name } : item)));
    };

    const removeFromQueue = (id) => {
        setQueue((prev) => prev.filter((item) => item.id !== id));
    };

    const clearQueue = () => {
        setQueue([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const submitUpload = async () => {
        if (!queue.length || uploading) return null;

        const invalid = queue.find((item) => !item.name?.trim());
        if (invalid) {
            badToast("Preencha o nome de todos os arquivos");
            return null;
        }

        setUploading(true);
        let okCount = 0;
        let failCount = 0;

        try {
            for (let i = 0; i < queue.length; i += 1) {
                const item = queue[i];
                const contentType = item.file.type || "application/octet-stream";
                setProgress({ loaded: 0, total: item.file.size, percent: 0, etaSec: Infinity });
                setUploadPhase(`Preparando ${i + 1}/${queue.length}: ${item.file.name}`);

                const presignData = await adapter.attachments.presign(user?.token, productData?._id, {
                    name: item.name.trim(),
                    contentType,
                    size: item.file.size,
                    originalName: item.file.name,
                });
                if (presignData?.type !== "success") {
                    failCount += 1;
                    badToast(presignData?.message || `Falha ao preparar: ${item.file.name}`);
                    continue;
                }

                setUploadPhase(`Enviando ${i + 1}/${queue.length}: ${item.file.name}`);
                try {
                    await uploadWithProgress(presignData.uploadUrl, item.file, contentType, setProgress);
                } catch (err) {
                    failCount += 1;
                    badToast(err?.message || `Falha no upload: ${item.file.name}`);
                    continue;
                }

                setUploadPhase(`Finalizando ${i + 1}/${queue.length}: ${item.file.name}`);
                setProgress((prev) => (prev ? { ...prev, percent: 100, etaSec: 0 } : prev));

                const completeData = await adapter.attachments.complete(
                    user?.token,
                    productData?._id,
                    presignData.attachmentId,
                );
                if (completeData?.type !== "success") {
                    failCount += 1;
                    badToast(completeData?.message || `Falha ao finalizar: ${item.file.name}`);
                    continue;
                }
                okCount += 1;
            }

            if (okCount) {
                goodToast(okCount === 1 ? "Anexo enviado!" : `${okCount} anexos enviados!`);
                clearQueue();
                refresh();
            }
            if (failCount && !okCount) {
                badToast("Nenhum arquivo foi enviado");
            }
        } finally {
            setUploading(false);
            setProgress(null);
            setUploadPhase("");
        }
    };

    const handleDownload = async (attachment) => {
        setBusy(true);
        try {
            const data = await adapter.attachments.download(user?.token, productData?._id, attachment._id);
            if (data?.type !== "success" || !data?.downloadUrl) {
                badToast(data?.message || "Erro ao baixar anexo");
                return null;
            }

            const response = await fetch(data.downloadUrl);
            if (!response.ok) {
                badToast(`Erro ao baixar arquivo (${response.status})`);
                return null;
            }
            const blob = await response.blob();
            const filename = attachment?.originalName || attachment?.name || "anexo";
            triggerBrowserDownload(blob, filename);
        } catch (err) {
            badToast(err?.message || "Erro ao baixar anexo");
        } finally {
            setBusy(false);
        }
    };

    const handleRename = async (attachmentId, currentName) => {
        const newName = window.prompt("Novo nome", currentName);
        if (!newName || newName.trim() === "" || newName === currentName) {
            return null;
        }
        setBusy(true);
        const data = await adapter.attachments.rename(user?.token, productData?._id, attachmentId, newName.trim());
        if (data?.type !== "success") {
            badToast(data?.message || "Erro ao renomear anexo");
            setBusy(false);
            return null;
        }
        goodToast("Anexo renomeado!");
        refresh();
        setBusy(false);
    };

    const handleDelete = async (attachmentId) => {
        if (!window.confirm("Excluir este anexo?")) {
            return null;
        }
        setBusy(true);
        const data = await adapter.attachments.remove(user?.token, productData?._id, attachmentId);
        if (data?.type !== "success") {
            badToast(data?.message || "Erro ao excluir anexo");
            setBusy(false);
            return null;
        }
        goodToast("Anexo excluído!");
        refresh();
        setBusy(false);
    };

    if (!more) {
        return (
            <div className="border border-[#666] p-2 m-4 cursor-pointer bg-monao" onClick={() => setMore(!more)}>
                <div className="flex items-center gap-2 w-fit m-auto">
                    {
                        attachments?.length ?
                            <div className="w-6 h-2 rounded-full animate-pulse bg-primary"></div>
                            : null
                    }
                    <h2 className="text-center text-lg text-white">Anexos</h2>
                    {
                        attachments?.length ?
                            <div className="w-6 h-2 rounded-full animate-pulse bg-primary"></div>
                            : null
                    }
                    <p className="text-sm mb-4 text-primary">{attachments?.length ? `[${attachments.length}]` : null}</p>
                    <div><ChevronIcon fill="#fff" width="30px" /></div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative border border-[#666] p-2 m-4 bg-monao pb-10">
            <h2 className="text-center text-lg mb-4 text-white">Anexos</h2>
            <div className="transform rotate-180 absolute top-2 right-2 cursor-pointer" onClick={() => setMore(false)}>
                <ChevronIcon fill="#d59106" width="30px" />
            </div>

            <div className="max-h-[320px] mt-4 overflow-y-scroll">
                {
                    attachments?.length ?
                        attachments.map((a) => (
                            <div key={a._id} className="border border-primary bg-[#000] px-2 py-4 mb-2">
                                <p className="mb-2 text-center font-bold text-white">{a?.name}</p>
                                <p className="mb-1 text-center text-sm text-gray-300">{formatarDataCompleta(a?.date)}</p>
                                {a?.size ? (
                                    <p className="mb-4 text-center text-xs text-gray-400">{formatBytes(a.size)}</p>
                                ) : (
                                    <div className="mb-4" />
                                )}
                                <div className="flex justify-center gap-2 flex-wrap">
                                    <Button classes="bg-primary hover:brightness-110" click={() => handleDownload(a)} disabled={busy || uploading}>
                                        Baixar
                                    </Button>
                                    <Button classes="bg-primary hover:brightness-110" click={() => handleRename(a._id, a?.name)} disabled={busy || uploading}>
                                        Renomear
                                    </Button>
                                    <Button classes="bg-red-600 text-white hover:bg-red-500" click={() => handleDelete(a._id)} disabled={busy || uploading}>
                                        Excluir
                                    </Button>
                                </div>
                            </div>
                        ))
                        :
                        <p className="text-center text-gray-400">Nenhum anexo</p>
                }
            </div>

            <div className="flex flex-col gap-3 px-4 my-4 mt-10 max-w-xl m-auto">
                <input
                    ref={fileInputRef}
                    className="hidden"
                    type="file"
                    multiple
                    disabled={uploading}
                    onChange={handleFileChange}
                />

                <div
                    role="button"
                    tabIndex={0}
                    aria-label="Área para enviar arquivos"
                    className={`border-2 border-dashed rounded-md px-4 py-8 text-center transition-colors cursor-pointer select-none ${
                        dragOver
                            ? "border-primary bg-[#d5910622]"
                            : "border-[#888] bg-[#00000055] hover:border-primary"
                    } ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            fileInputRef.current?.click();
                        }
                    }}
                    onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOver(true);
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOver(true);
                    }}
                    onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOver(false);
                    }}
                    onDrop={handleDrop}
                >
                    <p className="text-white font-semibold">Arraste arquivos aqui</p>
                    <p className="text-gray-300 text-sm mt-2">ou clique para selecionar vários</p>
                    <p className="text-gray-400 text-xs mt-3">Até 50 MB por arquivo · qualquer tipo</p>
                </div>

                {queue.length ? (
                    <div className="border border-primary bg-[#000] p-3 rounded-sm max-h-56 overflow-y-auto">
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-primary text-sm">{queue.length} arquivo(s) na fila</p>
                            <button
                                type="button"
                                className="text-xs text-gray-300 underline disabled:opacity-50"
                                onClick={clearQueue}
                                disabled={uploading}
                            >
                                Limpar fila
                            </button>
                        </div>
                        {queue.map((item) => (
                            <div key={item.id} className="border border-[#444] p-2 mb-2 last:mb-0">
                                <p className="text-xs text-gray-400 break-all mb-1">{item.file.name} · {formatBytes(item.file.size)}</p>
                                <div className="flex gap-2 items-center">
                                    <input
                                        className="flex-1 text-black px-2 py-1 rounded-sm border border-primary bg-white text-sm"
                                        type="text"
                                        value={item.name}
                                        disabled={uploading}
                                        placeholder="Nome do anexo"
                                        onChange={(e) => updateQueueName(item.id, e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="text-xs text-red-400 underline whitespace-nowrap disabled:opacity-50"
                                        onClick={() => removeFromQueue(item.id)}
                                        disabled={uploading}
                                    >
                                        Remover
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}

                {uploading && progress ? (
                    <div className="border border-primary bg-[#000] p-3 rounded-sm">
                        <p className="text-primary text-sm mb-2 break-all">{uploadPhase}</p>
                        <div className="w-full h-3 bg-[#333] rounded-sm overflow-hidden">
                            <div
                                className="h-full bg-primary transition-all duration-150"
                                style={{ width: `${progress.percent}%` }}
                            />
                        </div>
                        <div className="flex justify-between text-xs text-gray-300 mt-2 gap-2 flex-wrap">
                            <span>{progress.percent}%</span>
                            <span>
                                {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
                            </span>
                            <span>resta ~ {formatEta(progress.etaSec)}</span>
                        </div>
                    </div>
                ) : null}

                <Button
                    classes="bg-primary hover:brightness-110 w-full py-2"
                    click={submitUpload}
                    disabled={!queue.length || uploading || busy}
                >
                    {uploading
                        ? "Enviando…"
                        : queue.length > 1
                            ? `Enviar ${queue.length} anexos`
                            : "Enviar anexo"}
                </Button>
            </div>
        </div>
    );
};

export default ProductAttachments;
